import { useSuspenseQuery } from "@apollo/client";
import {
  CurrencyAmountUI,
  DataTable,
  Number,
  PrimaryFullScreenSpinner,
} from "@repo/ui";
import { isCurrency, NotFoundError } from "@repo/utils";
import { createFileRoute, useParams } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { Suspense } from "react";
import { graphql } from "~/gql/client";
import type { AdminLedgerAccountBalanceRowFragment } from "~/gql/client/graphql";
import { opsHead } from "~/head";

export const Route = createFileRoute(
  "/__auth/ledger/accounts_/$accountId/balances",
)({
  head: () => opsHead("Ledger Account Balances"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

const columns: ColumnDef<AdminLedgerAccountBalanceRowFragment>[] = [
  {
    accessorKey: "unit",
    header: "Currency / Unit",
  },
  {
    accessorKey: "total",
    header: "Total",
    cell: ({ row }) =>
      isCurrency(row.original.unit) ? (
        <CurrencyAmountUI
          amount={row.original.amounts.total}
          currency={row.original.unit}
          hideCurrency
        />
      ) : (
        <Number
          value={row.original.amounts.total}
          format={Number.format.units(row.original.amounts.total)}
        />
      ),
  },
  {
    accessorKey: "available",
    header: "Available",
    cell: ({ row }) =>
      isCurrency(row.original.unit) ? (
        <CurrencyAmountUI
          amount={row.original.amounts.available}
          currency={row.original.unit}
          hideCurrency
        />
      ) : (
        <Number
          value={row.original.amounts.available}
          format={Number.format.units(row.original.amounts.available)}
        />
      ),
  },
  {
    accessorKey: "pendingCredits",
    header: "Pending Credits",
    cell: ({ row }) =>
      isCurrency(row.original.unit) ? (
        <CurrencyAmountUI
          amount={row.original.amounts.pendingCredits}
          currency={row.original.unit}
          hideCurrency
        />
      ) : (
        <Number
          value={row.original.amounts.pendingCredits}
          format={Number.format.units(row.original.amounts.pendingCredits)}
        />
      ),
  },
];

function View() {
  const { accountId } = useParams({ strict: false });
  const { data } = useSuspenseQuery(
    graphql(`
      query AdminLedgerAccountBalance($ledgerAccountId: ID!) {
        node(id: $ledgerAccountId) {
          id
          ... on LedgerAccount {
            balances {
              ...AdminLedgerAccountBalanceRow
            }
          }
        }
      }

      fragment AdminLedgerAccountBalanceRow on LedgerAccountBalance {
        id
        unit
        amounts {
          id
          available
          total
          pendingCredits
        }
      }
    `),
    {
      variables: {
        ledgerAccountId: accountId ?? "",
      },
    },
  );

  const la = data.node;
  if (
    la?.__typename !== "AssetLedgerAccount" &&
    la?.__typename !== "BeneficiaryLedgerAccount" &&
    la?.__typename !== "OrganisationLedgerAccount" &&
    la?.__typename !== "FXMarginProviderLedgerAccount" &&
    la?.__typename !== "CustodyLedgerAccount"
  ) {
    throw new NotFoundError();
  }

  return (
    <div>
      <DataTable hidePagination data={la.balances ?? []} columns={columns} />
    </div>
  );
}
